import { AutomationModel } from '../ast/AutomationModel.js';
import { AutomationTrigger } from '../ast/AutomationTrigger.js';
import { AutomationAction } from '../ast/AutomationAction.js';
import { AutomationCondition } from '../ast/AutomationCondition.js';
import { AutomationValue } from '../ast/AutomationValue.js';
import { AutomationBinding } from '../ast/AutomationBinding.js';
import { buildPropertyExpression } from '../generators/propertyExpression.js';
import { getAutomationMetaByType } from '../schema/WidgetAutomationMeta.js';

/**
 * loadAutomationModels
 *
 * Unified model loader — handles both the new persisted format and the legacy
 * flat table formats transparently.
 *
 * Resolution order:
 *   1. hass_config.automation[] exists and is non-empty
 *      → deserialise AutomationModel[] directly (pre-rename binding JSON,
 *        if any, is migrated to the current strategy/rules shape first —
 *        see _migrateLegacyBindingData)
 *   2. Otherwise → migrate from hass_config.events[] (legacy format)
 *
 * State bindings: legacy hass_config.properties[] rows are migrated into
 * binding models whenever the persisted automation carries no bindings yet —
 * properties and events unify into one Automation Editor.
 *
 * This is the single entry-point for any code that needs to read automation
 * models from a widget's hass_config, and the single place legacy binding
 * knowledge (source/onValue/offValue) is converted to the current
 * strategy/rules model — see docs/Binding_Architecture_FINAL.md §8.
 *
 * @param {object} options
 * @param {number} [options.page_id]
 * @param {number} [options.hasp_id]
 * @param {object} [options.object]
 * @param {object} options.hass_config
 * @returns {AutomationModel[]}
 */
export function loadAutomationModels({ page_id, hasp_id, object, hass_config }) {
  const automation = hass_config?.automation;

  const models = (Array.isArray(automation) && automation.length > 0)
    ? automation.map(d => AutomationModel.fromJSON(_migrateLegacyAutomationData(d)))
    : migrateHassConfigToAst({ page_id, hasp_id, object, hass_config });

  // Migrate legacy property rows unless bindings are already persisted
  if (!models.some(m => m.bindings.length > 0)) {
    const bindingModels = migratePropertiesToBindings({ page_id, hasp_id, object, hass_config });
    models.unshift(...bindingModels);
  }

  return models;
}

/**
 * Pre-rename → current binding shape for one serialized AutomationModel.
 * Converts `source: 'entity'|'toggle'|'template'|'constant'` to
 * `strategy: 'state'|'mapping'|'template'|'constant'`, and
 * `onValue`/`offValue` to the two-entry `rules[]` shape. Already-current
 * data (has `strategy`) passes through unchanged. This is the one place
 * that knows the pre-rename binding field names — AutomationBinding's
 * constructor still accepts them too (a defensive fallback for any input
 * that reaches it without going through this loader), but nothing in this
 * codebase relies on that fallback anymore.
 * @private
 */
function _migrateLegacyAutomationData(data) {
  if (!data?.bindings?.length) return data;
  return { ...data, bindings: data.bindings.map(_migrateLegacyBindingData).map(_normalizeBindingFormatter) };
}

/**
 * @private — one-time compatibility upgrade for binding data that
 * predates the formatter field. AutomationBinding itself stays
 * domain-agnostic (always defaults formatter to 'none'); this is the one
 * place that knows attribute:'rgb_color' used to imply hex-conversion
 * before the Color formatter existed, so byte-identical output survives
 * for projects saved before this phase. No strategy check — a 'mapping'
 * binding with an rgb_color attribute degrades to generateState() too
 * (see bindingGenerators.js), so it needs the same treatment. Unlike
 * _migrateLegacyBindingData, this runs unconditionally — most real
 * existing bindings already have `strategy` (from the earlier rename)
 * and would otherwise skip that function's `!binding.strategy` guard.
 */
function _isColorProperty(property) {
  return property === 'color' || property?.endsWith('_color');
}

function _normalizeBindingFormatter(binding) {
  if (!binding || binding.formatter) return binding;
  if (_isColorProperty(binding.property) && binding.attribute &&
      ['rgb_color', 'rgbw_color', 'rgbww_color', 'hs_color', 'xy_color'].includes(binding.attribute)) {
    return { ...binding, formatter: { type: 'color', params: {} } };
  }
  return binding;
}

/**
 * @private — same compatibility mapping as _normalizeBindingFormatter,
 * for the one construction site that builds an AutomationBinding
 * directly from a legacy row rather than from raw JSON (see
 * migratePropertiesToBindings below).
 */
function _formatterForLegacyAttribute(attribute, property) {
  return _isColorProperty(property) &&
    ['rgb_color', 'rgbw_color', 'rgbww_color', 'hs_color', 'xy_color'].includes(attribute)
    ? { type: 'color', params: {} }
    : undefined;
}

/** @private */
function _migrateLegacyBindingData(binding) {
  if (!binding || binding.strategy) return binding;

  const LEGACY_SOURCE_TO_STRATEGY = { entity: 'state', toggle: 'mapping', template: 'template', constant: 'constant' };
  const migrated = { ...binding, strategy: LEGACY_SOURCE_TO_STRATEGY[binding.source] ?? binding.source };
  delete migrated.source;

  if (binding.onValue !== undefined || binding.offValue !== undefined) {
    migrated.rules = [
      { when: { operator: '==', value: 'on' }, result: binding.onValue ?? '' },
      { when: null, result: binding.offValue ?? '' },
    ];
    delete migrated.onValue;
    delete migrated.offValue;
  }

  return migrated;
}

/**
 * migratePropertiesToBindings
 *
 * Converts legacy hass_config.properties[] rows (the HA properties table)
 * into binding models — one AutomationModel with one AutomationBinding per
 * row.
 *
 * Simple rows (entity + attribute + filter + fallback) become structured
 * 'state' bindings. Rows using advanced legacy fields (suffix, explicit
 * on/off states) are frozen into 'template' bindings carrying the exact
 * Jinja2 expression the generator would have produced — output is identical
 * either way.
 *
 * @param {object} options — same shape as loadAutomationModels
 * @returns {AutomationModel[]}
 */
export function migratePropertiesToBindings({ page_id, hasp_id, object, hass_config }) {
  const rows    = (hass_config?.properties ?? []).filter(p => p && p.property);
  const objType = object?.obj ?? '';

  return rows.map(row => {
    const usesAdvancedFields =
      (row.suffix ?? '') !== '' || (row.on_state ?? '') !== '' || (row.off_state ?? '') !== '';

    const binding = usesAdvancedFields
      ? new AutomationBinding({
          property: row.property,
          strategy: 'template',
          template: buildPropertyExpression(row, objType),
        })
      : new AutomationBinding({
          property:  row.property,
          strategy:  'state',
          entityId:  row.entityId  ?? '',
          attribute: row.attribute ?? '',
          filter:    row.text      ?? '',
          fallback:  row.fallback  ?? '',
          formatter: _formatterForLegacyAttribute(row.attribute, row.property),
        });

    return new AutomationModel({
      widgetPageId: page_id ?? null,
      widgetHaspId: hasp_id ?? null,
      widgetType:   objType || null,
      bindings:     [binding],
    });
  });
}

/**
 * bindingModelsToPropertyRows
 *
 * Best-effort mirror of binding models back into the legacy
 * hass_config.properties row shape. Kept in sync on every editor commit so
 * legacy consumers (tree view, entity collection during export) keep
 * working. Template/constant bindings have no row equivalent and are
 * omitted — YAML generation reads the binding models directly.
 *
 * @param {AutomationModel[]} models
 * @returns {object[]}
 */
export function bindingModelsToPropertyRows(models) {
  return models
    .flatMap(m => m.bindings)
    .filter(b => b.property && b.strategy === 'state')
    .map(b => ({
      property:  b.property,
      entityId:  b.entityId,
      attribute: b.valueSource === 'attribute' ? b.attribute : '',
      text:      b.filter,
      suffix:    '',
      fallback:  b.fallback,
      on_state:  '',
      off_state: '',
    }));
}

/**
 * migrateHassConfigToAst
 *
 * Converts legacy hass_config.events[] (the flat HA-specific list) into an
 * array of platform-independent AutomationModel instances.
 *
 * Migration rules:
 *  - Each event row produces one AutomationModel.
 *  - Widgets with exactly one declared event (Schema V2 events, via
 *    getAutomationMetaByType), or an explicit legacyTrigger compatibility
 *    field, never showed the eventType picker in the legacy Events table —
 *    their rows are filtered by `service` only and always trigger that event.
 *  - Widgets that also declare automation.legacyRowIndexVariable (e.g.
 *    msgbox — one row per button, not per event type) additionally get a
 *    CompareCondition (variable == row index) per row.
 *  - All other widgets are filtered by `eventType && service`.
 *  - Entity IDs are stored as parameters with key 'entity_id'.
 *    Single entity  → AutomationValue.literal(id)
 *    Multiple       → AutomationValue.list([id1, id2, ...])
 *  - openhasp rows carry `_providerExtra` with popup/delete metadata that
 *    cannot be expressed in the generic AST.
 *
 * This function never modifies the input — it always creates new instances.
 *
 * @param {object} options
 * @param {number} options.page_id
 * @param {number} options.hasp_id
 * @param {object} options.object       — HaspObject instance (.obj, .comment)
 * @param {object} options.hass_config  — { properties, events }
 * @returns {AutomationModel[]}
 */
export function migrateHassConfigToAst({ page_id, hasp_id, object, hass_config }) {
  const rawEvents = hass_config?.events ?? [];
  const meta        = getAutomationMetaByType(object?.obj);
  const soleTrigger = meta?.legacyTrigger
    ?? (meta?.triggers?.length === 1 ? meta.triggers[0] : null);
  const rowIndexVar = meta?.legacyRowIndexVariable ?? null;
  const widgetId    = `p${page_id}b${hasp_id}`;

  // ── Filter rows ─────────────────────────────────────────────────────────────
  // A widget with exactly one declared trigger never showed the eventType
  // picker in the legacy Events table (nothing to choose); its rows are
  // filtered by service alone. All other widgets need both eventType and
  // service for a meaningful automation.
  const visibleRows = soleTrigger
    ? rawEvents.filter(evt => evt.service)
    : rawEvents.filter(evt => evt.eventType && evt.service);

  return visibleRows.map((evt, index) => {
    // ── Trigger ───────────────────────────────────────────────────────────────
    // A widget with a sole trigger always fires that one event; others use
    // the stored eventType.
    const trigger = new AutomationTrigger({
      source:   'widget',
      event:    soleTrigger ?? evt.eventType,
      widgetId,
    });

    // ── Parameters: data items ────────────────────────────────────────────────
    // Normalise legacy single-key format to the data array format.
    const legacyData =
      evt.data && evt.data.length > 0
        ? evt.data
        : evt.data_key
          ? [{ key: evt.data_key, value: '{{ val }}' }]
          : [];

    const parameters = legacyData.map(d => ({
      key:   d.key,
      value: new AutomationValue({ kind: 'template', template: String(d.value) }),
    }));

    // ── Parameters: entity_id ─────────────────────────────────────────────────
    // Entities are stored as a generic parameter — not a HA-specific field.
    // Generators decide how to route the entity_id parameter to their format.
    const entities =
      (evt.entities || []).filter(Boolean).length > 0
        ? evt.entities.filter(Boolean)
        : evt.entityId
          ? [evt.entityId]
          : [];

    if (entities.length === 1) {
      parameters.push({ key: 'entity_id', value: AutomationValue.literal(entities[0]) });
    } else if (entities.length > 1) {
      parameters.push({ key: 'entity_id', value: AutomationValue.list(entities) });
    }

    // ── Action ────────────────────────────────────────────────────────────────
    const action = new AutomationAction({
      type:       'call_service',
      provider:   evt.service,
      service:    evt.action ?? '',
      parameters,
    });

    // ── Conditions (row-indexed widgets only) ─────────────────────────────────
    // Widgets that declare automation.legacyRowIndexVariable (e.g. msgbox's
    // one-row-per-button rows) get one CompareCondition per row, comparing
    // that variable to the row's 0-based index. Modelled as a generic
    // CompareCondition — no widget-specific condition types.
    const conditions = rowIndexVar
      ? [
          new AutomationCondition({
            left:     AutomationValue.reference(rowIndexVar),
            operator: '==',
            right:    AutomationValue.literal(String(index)),
          }),
        ]
      : [];

    // ── Provider-specific extra (openhasp only) ───────────────────────────────
    // openhasp popup and delete actions require template page references that
    // cannot be expressed in the generic AST. They are carried opaquely so
    // only the HA Generator needs to know about them.
    let providerExtra = null;
    if (evt.service === 'openhasp') {
      providerExtra = {
        action:              evt.action,
        _popupTemplateId:    evt._popupTemplateId    ?? null,
        _popupTargetPageId:  evt._popupTargetPageId  ?? null,
        _popupEntity:        evt._popupEntity         ?? null,
        _popupRootObjectId:  evt._popupRootObjectId   ?? null,
        _deleteTargetPageId: evt._deleteTargetPageId  ?? null,
        _deleteObjectId:     evt._deleteObjectId       ?? null,
      };
    }

    return new AutomationModel({
      widgetPageId:   page_id,
      widgetHaspId:   hasp_id,
      widgetType:     object?.obj ?? null,
      triggers:       [trigger],
      actions:        [action],
      conditions,
      _providerExtra: providerExtra,
    });
  });
}
